import React, { useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { SxProps } from '@mui/system';
import { addFileIcon, editIcon, warningSignIcon } from '@europrocurement/l2d-icons';
import {
    ClearButton,
    FlexyIconButton,
    useModal,
    ShortenDisplay,
} from '@europrocurement/flexy-components';
import {
    type ReglementsEtAcomptesCBJsonldAcompteRead,
    type ReglementsEtAcomptesCBJsonldReglementRead,
} from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { DateTime } from 'luxon';
import { UseKeycloakCheckRole } from '@europrocurement/l2d-keycloak';
import { FactureFormaliteApiObject, useFormaliteService } from '@europrocurement/l2d-domain';
import { useToaster } from '@europrocurement/l2d-modules/hooks';
import {
    ActionWidth,
    ActionWidthMobile,
    DateEnregistrementLibelle,
    DateEnregistrementWidth,
    DateEnregistrementWidthMobile,
    DocumentLieWidth,
    FactureLieeLibelle,
    FactureLieeWidth,
    FactureLieeWidthMobile,
    ModeWidth,
    MontantWidth,
    NoteLibelle,
    NoteWidth,
    NoteWidthMobile,
    ReferenceLibelle,
    ReferenceWidth,
    ReferenceWidthMobile,
    TransactionCBLibelle,
    TransactionCBWidth,
    TransactionCBWidthMobile,
} from './functions';
import { formatEuro } from '../ArticlePrefacturation/functions';
import ProvisionUpdate from '../ProvisionUpdate/ProvisionUpdate';
import { type DocumentGed } from '../../../hooks/useDossierDocumentFilesApi';

export type LigneProvisionReglementProps = {
    reglement: ReglementsEtAcomptesCBJsonldAcompteRead | ReglementsEtAcomptesCBJsonldReglementRead;
    sx?: SxProps;
    afterSubmit?: () => void;
    handleValidateInvoice: (invoice: FactureFormaliteApiObject) => Promise<void>;
    handleAddAttachmentFile?: (reglementId?: number) => void;
    reglementsDocs: DocumentGed[];
};

const LigneProvisionReglement: React.FunctionComponent<LigneProvisionReglementProps> = function (
    props,
) {
    const {
        reglement,
        sx,
        afterSubmit,
        handleValidateInvoice,
        handleAddAttachmentFile,
        reglementsDocs,
    } = props;
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const { handleApiResponse } = useToaster();
    const { modalActions } = useModal();
    const { palette } = useTheme();

    const roleChecker = UseKeycloakCheckRole();
    const { acomptesApi } = useFormaliteService();

    const deleteAcompte = async () => {
        if (isSubmitting) {
            return;
        }
        setIsSubmitting(true);
        const deleteRequest = acomptesApi.apiAcomptesIdDelete({
            id: reglement.id as unknown as string,
        });

        await handleApiResponse(deleteRequest, afterSubmit);
        setIsSubmitting(false);
    };

    return (
        <>
            <Box
                key={`reglement_${reglement.id}`}
                sx={{
                    display: {
                        xs: 'none',
                        sm: 'none',
                        md: 'none',
                        lg: 'flex',
                        xl: 'flex',
                    },

                    flexDirection: 'row',
                    width: '100%',
                    padding: '4px 8px 4px 4px',
                    gap: '0',
                    alignItems: 'center',
                    borderRadius: '16px',
                    color: 'text.gris50',
                    ...sx,
                }}
            >
                <Box
                    sx={{
                        width: ModeWidth,
                    }}
                >
                    {reglement.mode}
                </Box>
                <Box
                    sx={{
                        width: MontantWidth,
                    }}
                >
                    {reglement.montant ? formatEuro(Number(reglement.montant)) : formatEuro(0)}
                </Box>
                <Box
                    sx={{
                        width: NoteWidth,
                    }}
                >
                    {reglement.note || ' - '}
                </Box>
                <Box
                    sx={{
                        width: ReferenceWidth,
                    }}
                >
                    {reglement.reference || ' - '}
                </Box>
                <Box
                    sx={{
                        width: DateEnregistrementWidth,
                    }}
                >
                    {reglement.dreception
                        ? DateTime.fromISO(reglement.dreception).toLocaleString(DateTime.DATE_SHORT)
                        : ' - '}
                </Box>

                <Box
                    sx={{
                        width: TransactionCBWidth,
                    }}
                >
                    {reglement.transactioncb ? (
                        <ShortenDisplay
                            tooltipLabel={reglement.transactioncb}
                            text={reglement.transactioncb}
                            shortenLength={10}
                        />
                    ) : (
                        ' - '
                    )}
                </Box>
                <Box
                    sx={{
                        width: FactureLieeWidth,
                    }}
                >
                    {reglement?.detailsReglements
                        ? reglement.detailsReglements.map((detail) => {
                              const hasFacture =
                                  'facture' in detail &&
                                  detail.facture &&
                                  typeof detail.facture === 'object';
                              if (!hasFacture || !detail.facture || !detail.facture.numero)
                                  return ' - ';

                              const key = detail.facture.id ?? detail.facture.numero ?? undefined;

                              return (
                                  <Box
                                      sx={{
                                          display: 'flex',
                                          flexDirection: 'row',
                                          alignItems: 'center',
                                      }}
                                  >
                                      <Typography key={key}>
                                          {detail.facture.numero || ' - '}
                                      </Typography>
                                      {detail.facture.factureEnPreparation ? (
                                          <FlexyIconButton
                                              icon={warningSignIcon}
                                              iconOverwriteProps={{
                                                  color: palette.text.secondary,
                                                  size: '2xs',
                                                  sx: {
                                                      '&:hover': {
                                                          color: palette.warning.dark,
                                                      },
                                                  },
                                              }}
                                              onClick={() => {
                                                  if (!detail?.facture) return;

                                                  handleValidateInvoice(
                                                      detail.facture as unknown as FactureFormaliteApiObject,
                                                  );
                                              }}
                                              confirmation
                                              normalHoverColor={palette.warning.main}
                                              displayTooltip
                                              tooltipOverwriteProps={{
                                                  title: (
                                                      <Box
                                                          sx={{
                                                              display: 'flex',
                                                              flexDirection: 'column',
                                                              alignItems: 'start',
                                                              justifyContent: 'center',
                                                          }}
                                                      >
                                                          <Typography
                                                              variant="l2dcaption"
                                                              fontWeight="700"
                                                          >
                                                              Attention: Cette facture est
                                                              provisoire.
                                                          </Typography>
                                                          <Typography variant="l2dcaption">
                                                              La facture peut-être consolidée en
                                                              cliquant ici.
                                                          </Typography>
                                                      </Box>
                                                  ),
                                              }}
                                          />
                                      ) : null}
                                  </Box>
                              );
                          })
                        : ' - '}
                </Box>
                <Box
                    sx={{
                        width: DocumentLieWidth,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'start',
                        }}
                    >
                        {reglementsDocs.length}
                        {reglementsDocs && handleAddAttachmentFile ? (
                            <FlexyIconButton
                                icon={addFileIcon}
                                iconOverwriteProps={{
                                    color: palette.text.secondary,
                                    size: '2xs',
                                    sx: {
                                        '&:hover': {
                                            color: palette.primary.dark,
                                        },
                                    },
                                }}
                                onClick={() => {
                                    handleAddAttachmentFile(reglement.id);
                                }}
                                normalHoverColor={palette.primary.main}
                            />
                        ) : null}
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        width: ActionWidth,
                    }}
                >
                    {!reglement.transactioncb &&
                    roleChecker('api-formalite:acompte-update') &&
                    !!afterSubmit ? (
                        <FlexyIconButton
                            icon={editIcon}
                            iconOverwriteProps={{
                                color: palette.text.secondary,
                                size: '2xs',
                                sx: {
                                    '&:hover': {
                                        color: palette.primary.dark,
                                    },
                                },
                            }}
                            onClick={() => {
                                modalActions.call(
                                    <ProvisionUpdate
                                        afterSubmit={afterSubmit}
                                        acompte={reglement}
                                    />,
                                );
                            }}
                        />
                    ) : null}
                    {!reglement.transactioncb &&
                    roleChecker('api-formalite:acompte-update') &&
                    !!afterSubmit ? (
                        <ClearButton
                            confirmation
                            tooltip="Supprimer cet encaissement"
                            onClick={() => {
                                deleteAcompte();
                            }}
                        />
                    ) : null}
                </Box>
            </Box>

            <Box
                key={`reglement_${reglement.id}`}
                sx={{
                    display: {
                        xs: 'flex',
                        sm: 'flex',
                        md: 'flex',
                        lg: 'none',
                        xl: 'none',
                    },

                    flexDirection: 'column',
                    width: '100%',
                    padding: '4px 8px 4px 4px',
                    gap: '10px',
                    borderRadius: '16px',
                    alignItems: 'center',
                    color: 'text.gris50',
                    marginTop: '10px',
                    ...sx,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        width: '100%',
                        alignItems: 'center',
                    }}
                >
                    <Box
                        sx={{
                            width: ModeWidth,
                        }}
                    >
                        {reglement.mode}
                    </Box>
                    <Box
                        sx={{
                            width: MontantWidth,
                        }}
                    >
                        {reglement.montant ? formatEuro(Number(reglement.montant)) : formatEuro(0)}
                    </Box>
                    <Box
                        sx={{
                            width: NoteWidthMobile,
                        }}
                    >
                        {`${NoteLibelle} : ${reglement.note}`}
                    </Box>
                    <Box
                        sx={{
                            width: ReferenceWidthMobile,
                        }}
                    >
                        {`${ReferenceLibelle} : ${reglement.reference}`}
                    </Box>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        width: '100%',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                    }}
                >
                    <Box
                        sx={{
                            width: DateEnregistrementWidthMobile,
                        }}
                    >
                        {`${DateEnregistrementLibelle} : ${
                            reglement.dreception
                                ? DateTime.fromISO(reglement.dreception).toLocaleString(
                                      DateTime.DATE_SHORT,
                                  )
                                : "Pas de date d'enregistrement"
                        }`}
                    </Box>

                    <Box
                        sx={{
                            width: TransactionCBWidthMobile,
                        }}
                    >
                        {`${TransactionCBLibelle} : ${reglement.transactioncb}`}
                    </Box>
                    <Box
                        sx={{
                            width: FactureLieeWidthMobile,
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                        }}
                    >
                        <Typography component="span">{`${FactureLieeLibelle} : `}</Typography>
                        <Typography component="span">
                            {reglement?.detailsReglements
                                ? reglement.detailsReglements.map((detail) => {
                                      const hasFacture =
                                          'facture' in detail &&
                                          detail.facture &&
                                          typeof detail.facture === 'object';
                                      if (!hasFacture || !detail.facture || !detail.facture.numero)
                                          return null;

                                      const key =
                                          detail.facture.id ?? detail.facture.numero ?? undefined;

                                      if (detail.facture.factureEnPreparation) {
                                          return (
                                              <Typography
                                                  key={key}
                                                  variant="l2dh7"
                                                  color={palette.grey[500]}
                                              >
                                                  {detail.facture.numero}
                                              </Typography>
                                          );
                                      }
                                      return (
                                          <Typography key={key}>{detail.facture.numero}</Typography>
                                      );
                                  })
                                : null}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            width: ActionWidthMobile,
                        }}
                    >
                        {!reglement.transactioncb && !!afterSubmit ? (
                            <FlexyIconButton
                                icon={editIcon}
                                iconOverwriteProps={{
                                    color: palette.text.secondary,
                                    size: '2xs',
                                    sx: {
                                        '&:hover': {
                                            color: palette.primary.dark,
                                        },
                                    },
                                }}
                                onClick={() => {
                                    modalActions.call(
                                        <ProvisionUpdate
                                            afterSubmit={afterSubmit}
                                            acompte={reglement}
                                        />,
                                    );
                                }}
                            />
                        ) : null}
                        {!reglement.transactioncb && !!afterSubmit ? (
                            <ClearButton
                                confirmation
                                tooltip="Supprimer cet encaissement"
                                onClick={() => {
                                    deleteAcompte();
                                }}
                            />
                        ) : null}
                    </Box>
                </Box>
            </Box>
        </>
    );
};

export default LigneProvisionReglement;
